import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { CREATE_GIS_FILE_MUTATION, CREATE_GIS_FILE_UPLOAD_URL_MUTATION } from '../../operations';
import React, { useState } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { styles } from './styles';
import { useGetCountries } from '@/hooks/useGetCountries';
import { useGetRegions } from '@/hooks/useGetRegions';
import { useMutation } from '@apollo/client';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const GisFileModal = ({ open, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [getPresignedUrl] = useMutation(CREATE_GIS_FILE_UPLOAD_URL_MUTATION);
  const [createGisFile] = useMutation(CREATE_GIS_FILE_MUTATION);

  const { countries } = useGetCountries();
  const { regions } = useGetRegions();

  const resetForm = () => {
    setName('');
    setDescription('');
    setSelectedFile(null);
    setCountry(null);
    setRegion(null);
    setError('');
    setUploading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const uploadToS3 = async (presignedData, file) => {
    const { url, fields } = presignedData;
    const formData = new FormData();

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }

    return response;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setUploading(true);

    try {
      const presignedResponse = await getPresignedUrl({
        variables: {
          filename: selectedFile.name,
        },
      });
      const { uuid, s3Key, s3Bucket, signedUrl, errors } =
        presignedResponse.data.createGisFileUploadUrl;

      if (errors?.length) {
        throw new Error(errors[0]);
      }

      await uploadToS3(signedUrl, selectedFile);

      const createResponse = await createGisFile({
        variables: {
          uuid,
          s3Bucket,
          s3Key,
          name,
          description,
          countryId: country?.id,
          regionId: region?.id,
        },
      });

      if (createResponse.data.createGisFile.errors?.length) {
        throw new Error(createResponse.data.createGisFile.errors[0]);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload GIS File</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedFile ? selectedFile.name : 'No file selected'}
            </Typography>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
            >
              Upload file
              <VisuallyHiddenInput
                type="file"
                onChange={(event) => handleFileChange(event)}
                required
              />
            </Button>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name}
              value={country}
              onChange={(_, newValue) => {
                setCountry(newValue);
                if (newValue) {
                  setRegion(null);
                }
              }}
              disabled={!!region}
              renderInput={(params) => <TextField {...params} label="Country" />}
            />
            <Autocomplete
              options={regions}
              getOptionLabel={(option) => option.name}
              value={region}
              onChange={(_, newValue) => {
                setRegion(newValue);
                if (newValue) {
                  setCountry(null);
                }
              }}
              disabled={!!country}
              renderInput={(params) => <TextField {...params} label="Region" />}
            />
            {error && <Box sx={{ color: 'error.main', mt: 1 }}>{error}</Box>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={uploading}
            variant="contained"
            color="primary"
            sx={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!selectedFile || uploading}
            sx={styles.button}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
